/**
 * POINTER FEEDBACK FOR THE DRAWN CONTROLS.
 *
 * The hover states added on 11.9.2026 were written for the CSS button system — `.btn`, `.sheet-row`, `button.art`.
 * On 14.9. the lobby was rebuilt from the approved previews the owner's way: the control is a DRAWN PIECE and the
 * clickable element is a separate, invisible rectangle over it. From that day its buttons matched none of those
 * selectors, and every screen rebuilt since has lost hover the same way — 139 `.cut-hit` buttons now.
 *
 * CSS cannot fix it: the picture and the button are siblings, and there is no "the sibling that is this same control"
 * selector. So the pairing is done here, once, for the whole game: on hover, find the drawn piece the button covers
 * and mark it. Nothing is drawn in CSS — the mark only applies the brightness that `button.art:hover` already used.
 *
 * Guarded by (hover: hover) and (pointer: fine) exactly as the CSS block is, so a touch device never leaves a control
 * stuck bright after a tap.
 */
const ART = 'img.cut, img.layer, img.pf-head-art, img.piece, .art > img';
const MARK = 'cut-hover';

/** how much of `a` lies inside `b` */
function overlap(a: DOMRect, b: DOMRect): number {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  if (w <= 0 || h <= 0) return 0;
  return (w * h) / Math.max(1, a.width * a.height);
}

/** the drawn piece this hit rectangle belongs to: the one it covers most, inside the same screen */
function pieceFor(hit: HTMLElement): HTMLElement | null {
  const box = hit.getBoundingClientRect();
  const scope = hit.closest('.screen, .sheet, .sheet-backdrop') ?? document.body;
  let best: HTMLElement | null = null, score = 0.34;          // below a third covered it is not this button's art
  for (const el of scope.querySelectorAll<HTMLElement>(ART)) {
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) continue;
    const s = overlap(r, box);
    if (s > score) { score = s; best = el; }
  }
  return best;
}

let lit: HTMLElement | null = null;
const clear = () => { lit?.classList.remove(MARK); lit = null; };

export function initCutHover(): void {
  if (typeof window === 'undefined') return;
  if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  document.addEventListener('pointerover', (e) => {
    const hit = (e.target as HTMLElement | null)?.closest<HTMLElement>('.cut-hit');
    if (!hit || (hit as HTMLButtonElement).disabled || hit.getAttribute('aria-disabled') === 'true') { clear(); return; }
    const art = pieceFor(hit);
    if (art === lit) return;
    clear();
    if (art) { art.classList.add(MARK); lit = art; }
  }, { passive: true });
  document.addEventListener('pointerout', (e) => {
    const hit = (e.target as HTMLElement | null)?.closest<HTMLElement>('.cut-hit');
    if (hit) clear();
  }, { passive: true });
  // a view swap leaves no pointerout behind
  addEventListener('blur', clear);
  document.addEventListener('visibilitychange', clear);
}
